import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { api, ApiError } from "./api/client.ts";
import { authEnabled } from "./lib/session.ts";
import { Layout } from "./components/Layout.tsx";
import { Login } from "./pages/Login.tsx";
import { Intake } from "./pages/Intake.tsx";
import { Jobs } from "./pages/Jobs.tsx";
import { JobDetail } from "./pages/JobDetail.tsx";
import { Settings } from "./pages/Settings.tsx";

type AuthState =
  | { phase: "loading" }
  | { phase: "authed"; email: string }
  | { phase: "unauthed" }
  | { phase: "error"; message: string };

export function App() {
  const [state, setState] = useState<AuthState>({ phase: "loading" });

  useEffect(() => {
    api
      .me()
      .then((u) => setState({ phase: "authed", email: u.email }))
      .catch((e) => {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          setState({ phase: "unauthed" });
        } else {
          setState({ phase: "error", message: e instanceof Error ? e.message : String(e) });
        }
      });
  }, []);

  if (state.phase === "loading") {
    return (
      <div className="flex h-full items-center justify-center text-slate-400">Loading…</div>
    );
  }

  if (state.phase === "error") {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="card max-w-md p-6 text-center">
          <p className="font-semibold text-rose-700">Couldn’t reach the server</p>
          <p className="mt-2 text-sm text-slate-600">{state.message}</p>
        </div>
      </div>
    );
  }

  if (state.phase === "unauthed") {
    return <Login onSignedIn={(email) => setState({ phase: "authed", email })} authEnabled={authEnabled} />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout email={state.email} />}>
          <Route index element={<Navigate to="/jobs" replace />} />
          <Route path="/intake" element={<Intake />} />
          <Route path="/jobs" element={<Jobs />} />
          <Route path="/jobs/:id" element={<JobDetail />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/jobs" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
